import React, { useEffect, useState } from "react";
import { Spin, Empty } from "antd";
import "./InvoiceFormHistory.css";
import { GetInvoiceHistoryService } from "../../../../Services/InvoiceService/InvoiceService";
import { InvoiceHistoryEntry } from "./Invoiceformhistory.types";

interface InvoiceFormHistoryProps {
  itemId?: number;
}

// Turns a raw entry into the human-readable line + a colour for the timeline dot.
// Keeping this in one place means new action types only need one edit.
const describeEntry = (
  entry: InvoiceHistoryEntry
): { text: React.ReactNode; dotClass: string } => {
  const who = entry.performedBy || "Unknown";

  switch (entry.actionType) {
    case "SUBMITTED":
      return {
        text: (
          <>
            This item was uploaded via {entry.method ?? "the inbox"} by{" "}
            <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-blue",
      };
    case "FIELD_EDITED": {
      const change = entry.fieldChange;
      return {
        text: change ? (
          <>
            <strong>{change.fieldName}</strong> changed from "
            {change.oldValue ?? "—"}" to "{change.newValue ?? "—"}" by{" "}
            <strong>{who}</strong>
          </>
        ) : (
          <>A field was edited by <strong>{who}</strong></>
        ),
        dotClass: "dot-purple",
      };
    }
    case "PUBLISHED_MANUAL":
      return {
        text: (
          <>
            This item was published manually by <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-green",
      };
    case "PUBLISHED_AUTO":
      return {
        text: (
          <>
            This item was published via auto-publish by <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-green",
      };
    case "ARCHIVED":
      return {
        text: (
          <>
            This item was archived by <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-orange",
      };
    case "UNARCHIVED":
      return {
        text: (
          <>
            This item was unarchived by <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-orange",
      };
    case "VIEWED":
      return {
        text: (
          <>
            This item was viewed for the first time by <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-grey",
      };

      case "PAGE_SPLIT": {
      const split = entry.pageSplit;
      return {
        text: split ? (
          <>
            <strong>{split.sourceFileName}</strong> was split into{" "}
            <strong>{split.pageCount}</strong> page
            {split.pageCount === 1 ? "" : "s"} by <strong>{who}</strong>
          </>
        ) : (
          <>A document was split by <strong>{who}</strong></>
        ),
        dotClass: "dot-blue",
      };
    }
    case "MERGED": {
      const merge = entry.mergeInfo;
      if (merge?.perspective === "source") {
        return {
          text: (
            <>
              This item was merged into invoice{" "}
              <strong>{merge.invoiceIds[0]}</strong> by <strong>{who}</strong>
            </>
          ),
          dotClass: "dot-teal",
        };
      }
      return {
        text: merge && merge.invoiceIds.length > 0 ? (
          <>
            This item was created by merging invoice
            {merge.invoiceIds.length === 1 ? "" : "s"}{" "}
            <strong>{merge.invoiceIds.join(", ")}</strong> by <strong>{who}</strong>
          </>
        ) : (
          <>This item was created by merging invoices by <strong>{who}</strong></>
        ),
        dotClass: "dot-teal",
      };
    }
    case "UNMERGED": {
      const merge = entry.mergeInfo;
      if (merge?.perspective === "source") {
        return {
          text: (
            <>
              This item was restored from merged invoice{" "}
              <strong>{merge.invoiceIds[0]}</strong> by <strong>{who}</strong>
            </>
          ),
          dotClass: "dot-orange",
        };
      }
      return {
        text: merge && merge.invoiceIds.length > 0 ? (
          <>
            This merge was reversed, restoring invoice
            {merge.invoiceIds.length === 1 ? "" : "s"}{" "}
            <strong>{merge.invoiceIds.join(", ")}</strong>, by <strong>{who}</strong>
          </>
        ) : (
          <>This merge was reversed by <strong>{who}</strong></>
        ),
        dotClass: "dot-orange",
      };
    }
    case "RESTORED": {
      const restore = entry.restoreInfo;
      return {
        text: (
          <>
            This item was restored
            {restore?.from && <> from <strong>{restore.from}</strong></>}
            {restore?.to && <> to <strong>{restore.to}</strong></>} by{" "}
            <strong>{who}</strong>
          </>
        ),
        dotClass: "dot-green",
      };
    }
    default:
      return {
        text: <>Unknown action by <strong>{who}</strong></>,
        dotClass: "dot-grey",
      };
  }
};

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) return `Today at ${time}`;

  const day = date.toLocaleDateString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return day;
};

const InvoiceFormHistory = ({ itemId }: InvoiceFormHistoryProps) => {
  const [entries, setEntries] = useState<InvoiceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemId) return;

    let cancelled = false;
    setLoading(true);

    GetInvoiceHistoryService(itemId).then((res) => {
      if (cancelled) return;
      // Most recent action at the top (TS-06). GetInvoiceHistoryService
      // always resolves (never rejects) — a failed request just comes back
      // as [], which falls through to the empty state below.
      const sorted = [...res].sort(
        (a, b) =>
          new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
      );
      setEntries(sorted);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [itemId]);

  if (loading) {
    return (
      <div className="history-container history-centered">
        <Spin size="small" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="history-container history-centered">
        <Empty description="No history available for this document." />
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-heading">RECENT ACTIVITY</div>
      <ul className="history-timeline">
        {entries.map((entry) => {
          const { text, dotClass } = describeEntry(entry);
          return (
            <li key={entry.id} className="history-item">
              <span className={`history-dot ${dotClass}`} />
              <div className="history-item-content">
                <div className="history-item-text">{text}</div>
                <div className="history-item-time">
                  {formatTimestamp(entry.performedAt)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default InvoiceFormHistory;